import React, { useRef, useState, useLayoutEffect } from 'react';
import { mergeAttributes, Node } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { v4 as uuid } from 'uuid';
import ClickAwayListener from '@mui/base/ClickAwayListener';

import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import CustomPicklistOptions from './CustomPicklistOptions';

export const PicklistComponent = ({ node, updateAttributes, ...props }) => {
	const [anchorEl, setAnchorEl] = useState(null);

	const editRef = useRef(null);

	const attribute = node?.attrs;
	const code = attribute?.code;
	const display = attribute?.display;
	const value = attribute?.value;
	const uid = attribute?.uid;
	const fieldId = attribute?.fieldId;
	const hide = attribute?.hide?.toLowerCase() === 'true';
	const isReadOnly = props.isReadOnly || props.extension?.options?.isReadOnly;

	useLayoutEffect(() => {
		updateAttributes({ uid: uuid() });
	}, []);

	const handleUpdateValue = text => {
		updateAttributes({ value: text });
	};

	const handleOpen = event => {
		if (isReadOnly) {
			return;
		}
		setAnchorEl(anchorEl ? null : event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const isListOpen = Boolean(anchorEl);
	return (
		<NodeViewWrapper
			as="span"
			className="custom-picklist-wrapper"
			contentEditable={false}
			data-testid="CustomPicklist-Wrapper"
		>
			<Box component="span" data-type="custom-picklist">
				{hide ? (
					<Tooltip role="tooltip" title={display}>
						<Box
							className="custom-picklist-bookmark"
							component="span"
							contentEditable={false}
							data-code={code}
							data-display={display}
							data-testid="custom-picklist-bookmark"
							data-value={value}
						>
							{display}
						</Box>
					</Tooltip>
				) : (
					<ClickAwayListener onClickAway={() => handleClose()}>
						<Box
							sx={{
								display: 'inline-flex',
							}}
						>
							<Tooltip role="tooltip" title={!anchorEl ? display : ''}>
								<Box
									ref={editRef}
									className="custom-picklist"
									component="span"
									contentEditable={false}
									data-id="custom-picklist-field"
									data-testid="custom-picklist"
									data-uid={uid}
									onClick={handleOpen}
								>
									<Box>{value || display}</Box>
									<Box
										sx={{
											display: 'inline-flex',
											alignItems: 'center',
											justifyContent: 'center',

											'& svg': {
												transform: isListOpen ? 'rotate(180deg)' : undefined,
												transition: 'transform ease 400ms',
											},
										}}
									>
										<ExpandMoreOutlinedIcon />
									</Box>
								</Box>
							</Tooltip>
							<CustomPicklistOptions
								anchorEl={anchorEl}
								fieldId={fieldId}
								handleClose={handleClose}
								updateSelected={handleUpdateValue}
								value={value}
							/>
						</Box>
					</ClickAwayListener>
				)}
			</Box>
		</NodeViewWrapper>
	);
};

export default Node.create({
	name: 'custom-picklist',

	group: 'inline',

	inline: true,

	atom: true,

	draggable: true,

	addAttributes() {
		return {
			code: {
				default: null,
				parseHTML: element => element.getAttribute('data-code'),
				renderHTML: attributes => {
					if (!attributes.code) {
						return {};
					}

					return {
						'data-code': attributes.code,
					};
				},
			},
			display: {
				default: null,
				parseHTML: element => element.getAttribute('data-display'),
				renderHTML: attributes => {
					if (!attributes.display) {
						return {};
					}

					return {
						'data-display': attributes.display,
						title: attributes.display,
					};
				},
			},
			value: {
				default: null,
				parseHTML: element => element.getAttribute('data-value'),
				renderHTML: attributes => {
					if (!attributes.value) {
						return {};
					}

					return {
						'data-value': attributes.value,
					};
				},
			},
			hide: {
				default: null,
				parseHTML: element => element.getAttribute('data-hide'),
				renderHTML: attributes => {
					if (!attributes.hide) {
						return {};
					}

					return {
						'data-hide': attributes.hide,
					};
				},
			},
			uid: {
				default: null,
				parseHTML: element => element.getAttribute('data-uid'),
				renderHTML: attributes => ({
					'data-uid': attributes.uid,
				}),
			},
			fieldId: {
				default: '',
				parseHTML: element => element.getAttribute('data-fieldId'),
				renderHTML: attributes => {
					if (!attributes.fieldId) {
						return {};
					}

					return {
						'data-fieldId': attributes.fieldId,
					};
				},
			},
		};
	},

	parseHTML() {
		return [
			{
				tag: `span[data-type="${this.name}"]`,
			},
		];
	},

	renderHTML({ node, HTMLAttributes }) {
		const { value, display, hide } = node.attrs;
		const isHide = hide && JSON.parse(hide);
		const textNode = isHide ? display : value;
		return [
			'span',
			mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, { 'data-type': this.name }),
			textNode || '',
		];
	},

	renderText({ node }) {
		const { value, display, hide } = node.attrs;
		const isHide = hide && JSON.parse(hide);
		const textNode = isHide ? display : value;
		return textNode || '';
	},

	addOptions() {
		return {
			isReadOnly: false,
		};
	},

	addNodeView() {
		return ReactNodeViewRenderer(PicklistComponent, {
			attrs: {
				'data-drag-handle': this.name,
			},
		});
	},
});
